import {Box, Stack, ToggleButton, ToggleButtonGroup, Typography} from "@mui/material";
import type {TemporalResolver} from "../../../../../../sdo/Resolver.ts";
import {MatchModeRow, OperatorToggle, DateF, SummaryPill, SummaryPillRow, rowSx, labelSx, hintSx, colHeaderSx, toggleGroupSx, set, type ResolverEditorProps} from "./ResolverUtils.tsx";

function TemporalEditor({draft, editing, onChange}: ResolverEditorProps & { draft: TemporalResolver }) {
    if (!editing) {
        const fmtFrom = `${draft.fromMonth}/${draft.fromDay}/${draft.fromYear}` +
            (draft.dateType === "DATETIME" ? ` ${draft.fromHour}:${draft.fromMinute} ${draft.fromAP}` : "");
        const fmtTo = draft.dateRange === "RANGE"
            ? `${draft.toMonth}/${draft.toDay}/${draft.toYear}` +
              (draft.dateType === "DATETIME" ? ` ${draft.toHour}:${draft.toMinute} ${draft.toAP}` : "")
            : "";
        return (
            <Stack direction="column" spacing={0.5} sx={{alignItems: "center", width: "100%"}}>
                <SummaryPillRow>
                    <SummaryPill label="Date Type" value={draft.dateType === "DATETIME" ? "Date + Time" : "Date"}/>
                    <SummaryPill label="Range" value={draft.dateRange === "RANGE" ? "Range" : "Specific"}/>
                </SummaryPillRow>
                <SummaryPillRow>
                    <SummaryPill label="From" value={fmtFrom}/>
                    {draft.dateRange === "RANGE" && <SummaryPill label="To" value={fmtTo}/>}
                </SummaryPillRow>
            </Stack>
        );
    }

    const upd = (key: string, value: unknown) => set(draft, key, value, onChange);
    const showTime = draft.dateType === "DATETIME";
    const showRange = draft.dateRange === "RANGE";

    const gridCols = showTime
        ? "56px 72px 72px 80px 72px 72px 90px 1fr"
        : "56px 72px 72px 80px 1fr";

    return (
        <Stack direction="column" spacing={1} alignItems="center">
            <MatchModeRow draft={draft} editing={editing} onChange={onChange}/>
            <Stack direction="row" sx={rowSx}>
                <Typography sx={labelSx}>Match on</Typography>
                <OperatorToggle
                    value={draft.dateType}
                    options={[
                        {label: "Date", value: "DATE"},
                        {label: "Date + Time", value: "DATETIME"},
                    ]}
                    onChange={(v) => upd("dateType", v)}
                />
                <Typography sx={labelSx}>for</Typography>
                <OperatorToggle
                    value={draft.dateRange}
                    options={[
                        {label: "Specific Date", value: "SPECIFIC"},
                        {label: "Date Range", value: "RANGE"},
                    ]}
                    onChange={(v) => upd("dateRange", v)}
                />
            </Stack>
            {/* Column headers */}
            <Box sx={{display: "grid", gridTemplateColumns: gridCols, gap: "6px", padding: "2px 0 0"}}>
                <Box/>
                <Typography sx={colHeaderSx}>Month</Typography>
                <Typography sx={colHeaderSx}>Day</Typography>
                <Typography sx={colHeaderSx}>Year</Typography>
                {showTime && <Typography sx={colHeaderSx}>Hour</Typography>}
                {showTime && <Typography sx={colHeaderSx}>Minute</Typography>}
                {showTime && <Typography sx={colHeaderSx}>AM/PM</Typography>}
                <Box/>
            </Box>
            {/* From row */}
            <Box sx={{display: "grid", gridTemplateColumns: gridCols, gap: "6px", alignItems: "center"}}>
                <Typography sx={{...labelSx, textAlign: "center"}}>From</Typography>
                <DateF value={draft.fromMonth} placeholder="MM" onChange={(v) => upd("fromMonth", v)}/>
                <DateF value={draft.fromDay} placeholder="DD" onChange={(v) => upd("fromDay", v)}/>
                <DateF value={draft.fromYear} placeholder="YYYY" onChange={(v) => upd("fromYear", v)} width="80px"/>
                {showTime && <DateF value={draft.fromHour} placeholder="HH" onChange={(v) => upd("fromHour", v)}/>}
                {showTime && <DateF value={draft.fromMinute} placeholder="MM" onChange={(v) => upd("fromMinute", v)}/>}
                {showTime && (
                    <ToggleButtonGroup
                        exclusive size="small" value={draft.fromAP}
                        onChange={(_e, v) => { if (v) upd("fromAP", v); }}
                        sx={toggleGroupSx}
                    >
                        <ToggleButton value="AM" sx={{padding: "3px 10px !important"}}>AM</ToggleButton>
                        <ToggleButton value="PM" sx={{padding: "3px 10px !important"}}>PM</ToggleButton>
                    </ToggleButtonGroup>
                )}
                <Box/>
            </Box>
            {/* To row (only when RANGE) */}
            {showRange && (
                <Box sx={{display: "grid", gridTemplateColumns: gridCols, gap: "6px", alignItems: "center"}}>
                    <Typography sx={{...labelSx, textAlign: "center"}}>To</Typography>
                    <DateF value={draft.toMonth} placeholder="MM" onChange={(v) => upd("toMonth", v)}/>
                    <DateF value={draft.toDay} placeholder="DD" onChange={(v) => upd("toDay", v)}/>
                    <DateF value={draft.toYear} placeholder="YYYY" onChange={(v) => upd("toYear", v)} width="80px"/>
                    {showTime && <DateF value={draft.toHour} placeholder="HH" onChange={(v) => upd("toHour", v)}/>}
                    {showTime && <DateF value={draft.toMinute} placeholder="MM" onChange={(v) => upd("toMinute", v)}/>}
                    {showTime && (
                        <ToggleButtonGroup
                            exclusive size="small" value={draft.toAP}
                            onChange={(_e, v) => { if (v) upd("toAP", v); }}
                            sx={toggleGroupSx}
                        >
                            <ToggleButton value="AM" sx={{padding: "3px 10px !important"}}>AM</ToggleButton>
                            <ToggleButton value="PM" sx={{padding: "3px 10px !important"}}>PM</ToggleButton>
                        </ToggleButtonGroup>
                    )}
                    <Box/>
                </Box>
            )}
            <Typography sx={hintSx}>Use an asterisk (*) symbol in any field to accept any value</Typography>
        </Stack>
    );
}

export default TemporalEditor;
